import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Button, ButtonToolbar, Divider, Form, Loader, SelectPicker} from "rsuite";
import {parseISO} from "rsuite/cjs/utils/dateUtils";
import {getUsers} from "../../../../service/adminService";
import {getPdf, getUserPreview} from "../../../../service/userService";
import {generateDocx} from "../../../../service/docxService";
import {Packer} from "docx";

const formatDate = (date) => {
    return date.toLocaleDateString('en-us', { year:"numeric", month:"short"});
}

const lineItemStyling = {
    display: 'list-item',
    listStylePosition: 'inside',
    paddingLeft: '1rem',
    whiteSpace: 'pre-line',
};

const lineItemStylingNoBullet = {
    display: 'block',
    listStylePosition: 'inside',
    paddingLeft: '1rem',
    whiteSpace: 'pre-line',
};


// TODO mostly duplicated from Preview, need to collapse and use either userInfo from redux or selected user
//  redux selector cannot be conditional
const UserResumes = () => {

    // look at collapsing state if that makes preview iteration easier
    const [loading, setLoading] = useState(false);
    const [userCerts, setUserCerts] = useState([]);
    const [userDegrees, setUserDegrees] = useState([]);
    const [userExperience, setUserExperience] = useState([]);
    const [projectBuckets, setProjectBuckets] = useState({});
    const [skillBuckets, setSkillBuckets] = useState({});

    const [users, setUsers] = useState([]);
    const [usersForDropDown, setUsersForDropdown] = useState([]);

    const [selectedUser, setSelectedUser] = useState({});

    const [pdfGenerating, setPdfGenerating] = useState(false);
    const [docxGenerating, setDocxGenerating] = useState(false);

    const loadUsers = useCallback(() => {
        setLoading(true);
        getUsers().then((users) => {
            setUsers(users);
            setUsersForDropdown(users.map(ele => ({label: `${ele.lastName}, ${ele.firstName}`, value: ele.email})));
            setLoading(false);
        });
    }, []);

    const loadUserObjectOnSelect = useCallback((email) => {
        const user = users.find((user) => user.email === email);
        setSelectedUser(user);
    }, [users]);

    // load all user information
    useEffect(() => {
        setLoading(true);
        loadUsers();
        getUserPreview(selectedUser?.id).then((preview) => {
            preview.experience.forEach((exp) => {
                exp.startDate = parseISO(exp.startDate);
                exp.endDate = parseISO(exp.endDate);
            });
            preview.experience.sort((a, b) => (a.endDate < b.endDate) ? 1 : -1);
            preview.experience.sort((a, b) => (a.startDate < b.startDate) ? 1 : -1);
            setUserExperience(preview.experience);

            preview.projects.forEach((project) => {
                project.startDate = parseISO(project.startDate);
                project.endDate = parseISO(project.endDate);
                const matchingExp = preview.experience.filter((exp) => exp.id === project.expId)[0];
                project.company = matchingExp.company;
                project.position = matchingExp.position;
            });
            preview.projects.sort((a, b) => (a.endDate < b.endDate) ? 1 : -1);
            // bucket projects bby experience
            const localBuckets = {}
            for (let i = 0; i < preview.experience.length; i++) {
                if (localBuckets[preview.experience[i].id] === undefined) {
                    localBuckets[preview.experience[i].id] = [];
                }
                localBuckets[preview.experience[i].id].push(...preview.projects.filter(project => project.expId === preview.experience[i].id));
                localBuckets[preview.experience[i].id].forEach(element => {
                    element.splitResponsibilities = element.responsibilities.split('\n');
                });
            }
            setProjectBuckets(localBuckets);

            const skillBuckets = {}
            for (let i = 0; i < preview.skills.length; i++) {
                if (skillBuckets[preview.skills[i].skillType] === undefined) {
                    skillBuckets[preview.skills[i].skillType] = [];
                }
                skillBuckets[preview.skills[i].skillType].push(preview.skills[i].skillName);
            }
            setSkillBuckets(skillBuckets);

            preview.degrees.forEach((degree) => {
                degree.dateEarned = parseISO(degree.dateEarned);
            });
            setUserDegrees(preview.degrees);

            preview.certs.forEach((cert) => {
                cert.dateEarned = parseISO(cert.dateEarned);
            });
            // backend is not reverse sorting this correctly for some reason - do it here for now
            preview.certs.sort((a, b) => (a.dateEarned < b.dateEarned) ? 1 : -1);
            setUserCerts(preview.certs);
            setLoading(false);
        })
    }, [loadUsers, selectedUser?.id]);

    const generatePdf = useCallback(() => {
        setPdfGenerating(true);
        // this call does not unwrap the data so we can get header information
        getPdf(selectedUser?.email).then((response) => {
            const a = document.createElement("a");
            // payload is base64 encoded, using raw binary, streams were being stripped out of the document payload
            const binaryString = atob(response.data);
            const binaryLen = binaryString.length;
            const bytes = new Uint8Array(binaryLen);

            for (let i = 0; i < binaryLen; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }

            const file = new Blob([bytes], {type: 'application/pdf'});
            a.href = window.URL.createObjectURL(file);
            // pull out filename from content-disposition header
            a.download = /filename=(.*?)$/.exec(response.headers["content-disposition"])[1];
            a.click();
            setPdfGenerating(false);
        });
    }, [selectedUser?.email]);

    const genDocx = useCallback(() => {
        setDocxGenerating(true);
        const doc = generateDocx(selectedUser, skillBuckets, projectBuckets, userExperience, userDegrees, userCerts)
        Packer.toBlob(doc).then((blob) => {
            const a = document.createElement("a");
            a.href = window.URL.createObjectURL(blob);
            a.download = `${selectedUser.lastName}-${selectedUser.firstName}-${new Date().toISOString().split('T')[0]}.docx`;
            a.click();
            setDocxGenerating(false);
        });
    }, [skillBuckets, selectedUser, projectBuckets, userCerts, userDegrees, userExperience]);

    const displaySkills = useMemo(() => {
        const title = <h4>Technical Expertise</h4>;
        const skillLines = [];
        for (const key in skillBuckets) {
            skillLines.push(
                <div key={key}>
                    <b>{key}: </b>
                    {skillBuckets[key].join(', ')}
                </div>
            );
        }
        return (
            <>
                {title}
                {skillLines}
            </>
        );
    }, [skillBuckets]);

    const displayExperience = useMemo(() => {
        const title = <h4>Professional Experience</h4>;
        const experience = [];
        userExperience.forEach((exp, i, array) => {
            experience.push(
                <div key={`${exp.position}-${exp.id}`}>
                    <p style={{textAlign: 'left'}}><b>{exp.position},</b> {exp.company}
                        <span style={{float: 'right'}}>
                            {formatDate(exp.startDate)} - {exp.isPresent ? 'Present' : formatDate(exp.endDate)}
                        </span>
                    </p>
                    {projectBuckets[exp.id]?.map((project) =>{
                        const responSize = project.splitResponsibilities.length;
                        return (
                            <div key={`${project.projectName}-${project.id}`}>
                                <p style={{textAlign: 'left'}}>
                                    {
                                        (project.projectName !== '' && Object.keys(projectBuckets[exp.id]).length > 1) ?
                                            `${project.projectName} - ${formatDate(project.startDate)} - ` +
                                            (project.isPresent ? 'Present' : formatDate(project.endDate)) :
                                            null
                                    }
                                    {project.splitResponsibilities.map((respon) => {
                                        return <span key={respon} style={(responSize > 1) ? lineItemStyling : lineItemStylingNoBullet}>{respon}</span>;
                                    })}
                                </p>
                            </div>
                        );
                    })}
                </div>
            );
        })
        return (
            <>
                {title}
                {experience}
            </>
        );
    }, [projectBuckets, userExperience]);

    const displayEducation = useMemo(() => {
        const title = <h4>Education</h4>;
        return (
            <>
                {title}
                {userDegrees.map((degree) => {
                    return (
                        <div key={`${degree.institution}-${degree.id}`}>
                            <p style={{textAlign: 'left'}}><b>{` ${degree.degreeName} ${degree.major}, `}</b>
                                <span style={{float: 'right'}}>{formatDate(degree.dateEarned)}</span>
                                {degree.institution}
                            </p>
                        </div>
                    );
                })}
            </>
        );
    }, [userDegrees]);

    const displayCertifications = useMemo(() => {
        const title = <h4>Certifications</h4>;
        return (
            <>
                {title}
                {userCerts.map((cert) => {
                    return (
                        <div key={`${cert.certName}-${cert.id}`}>
                            <p style={{textAlign: 'left'}}><b>{cert.certName}</b>
                                <span style={{float: 'right'}}>{formatDate(cert.dateEarned)}</span>
                            </p>
                        </div>
                    );
                })}
            </>
        );
    }, [userCerts]);

    return useMemo(() => {
        return (
            <div className="main-content">
                <Form
                    layout="inline"
                >
                    <Form.Group>
                        <ButtonToolbar>
                            <Button
                                appearance="primary"
                                loading={pdfGenerating}
                                onClick={() => generatePdf()}
                                disabled={selectedUser?.email === undefined}
                            >
                                Generate Pdf
                            </Button>
                            <Button
                                style={{marginLeft: '10px'}}
                                appearance="primary"
                                loading={docxGenerating}
                                onClick={() => genDocx()}
                                disabled={selectedUser?.email === undefined}
                            >
                                Generate Docx
                            </Button>
                        </ButtonToolbar>
                    </Form.Group>
                    <Form.Group>
                        <Form.ControlLabel>Select User</Form.ControlLabel>
                        <Form.Control name="users"
                                      accepter={SelectPicker}
                                      data={usersForDropDown}
                                      onChange={loadUserObjectOnSelect}
                        />
                    </Form.Group>
                </Form>
                <Divider style={{marginTop: '0rem'}}/>
                {loading ? <Loader /> :
                    selectedUser?.email === undefined ? null :
                        <>
                            <h2>{selectedUser.firstName} {selectedUser.lastName}</h2>
                            info@synergistcomputing.com
                            {displaySkills}
                            {displayExperience}
                            {displayEducation}
                            {displayCertifications}
                        </>
                }
            </div>
        );
    }, [
        displayCertifications,
        displayEducation,
        displayExperience,
        displaySkills,
        docxGenerating,
        generatePdf,
        genDocx,
        loading,
        loadUserObjectOnSelect,
        pdfGenerating,
        selectedUser,
        usersForDropDown]);
};

export default UserResumes;